import React from "react";
import { Modal, Group, CloseButton, Box, Stack } from "@mantine/core";
import Logo from "../../common/Logo/Logo";
import ProgressiveSidebar from "./ProgressiveSidebar";
import { ProgressiveStatus } from "../types/onboarding";
import styles from "./ProgressiveWizardShell.module.css";

export type ProgressiveStep = "employees" | "services" | "payments";

interface ProgressiveWizardShellProps {
  opened: boolean;
  onClose: () => void;
  currentStep: ProgressiveStep;
  currentSubStep?: number;
  totalSubSteps?: number;
  employeesStatus: ProgressiveStatus;
  servicesStatus: ProgressiveStatus;
  paymentsStatus: ProgressiveStatus;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onStepClick?: (step: ProgressiveStep) => void;
  stepper?: React.ReactNode;
}

export default function ProgressiveWizardShell({
  opened,
  onClose,
  currentStep,
  currentSubStep = 1,
  totalSubSteps = 3,
  employeesStatus,
  servicesStatus,
  paymentsStatus,
  children,
  footer,
  onStepClick,
  stepper,
}: ProgressiveWizardShellProps) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      transitionProps={{ duration: 0 }}
      classNames={{
        content: styles.modalContent,
        body: styles.modalBody,
      }}
    >
      <Box className={styles.container}>
        {/* Header */}
        <Group justify="space-between" align="center" className={styles.header}>
          {stepper ? (
            <Box className={styles.stepperContainer}>{stepper}</Box>
          ) : (
            <Logo variant="horizontal" size="md" />
          )}
          <CloseButton onClick={onClose} size="lg" />
        </Group>

        {/* Main Content */}
        <Box className={styles.main}>
          {/* Sidebar - only show when stepper is NOT present */}
          {!stepper && (
            <Box className={styles.sidebar}>
              <ProgressiveSidebar
                currentStep={currentStep}
                employeesStatus={employeesStatus}
                servicesStatus={servicesStatus}
                paymentsStatus={paymentsStatus}
                onStepClick={onStepClick}
              />
            </Box>
          )}

          {/* Content Area */}
          <Stack
            gap={0}
            className={`${styles.content} ${stepper ? styles.contentFullWidth : ""}`}
          >
            {/* Progress Dots - only show when stepper is NOT present (landing/list pages) */}
            {!stepper && totalSubSteps && totalSubSteps > 1 && (
              <Group gap="xs" className={styles.progressDots}>
                {Array.from({ length: totalSubSteps }, (_, i) => (
                  <Box
                    key={i}
                    className={`${styles.dot} ${i + 1 === currentSubStep ? styles.dotActive : ""} ${i + 1 < currentSubStep ? styles.dotCompleted : ""}`}
                  />
                ))}
              </Group>
            )}

            {/* Step Content */}
            <Box className={styles.stepContent}>{children}</Box>
          </Stack>
        </Box>

        {/* Footer */}
        {footer && (
          <Group justify="flex-end" align="center" className={styles.footer}>
            {footer}
          </Group>
        )}
      </Box>
    </Modal>
  );
}
